import { useCallback, useEffect, useState } from 'react';
import { Banner, NotificationDispatch, Tab, TabList, TabPanel, Tabs, Title } from '@tryhackme/thm-ui-components';
import { StyledTabContent, StyledTaggingContainer } from 'src/features/admin/tagging/tagging.styles';
import { SEO } from 'src/common/components';
import { Tags } from 'src/features/admin/tagging/components/tabs/tags';
import { TagCategories } from 'src/features/admin/tagging/components/tabs/tag-categories';
import { CreateTagRequest, TagCategory, UpdateTagCategoryRequest, UpdateTagRequest } from 'src/features/admin/tagging/tagging.types';
import { useCreateTagMutation, useCreateTagCategoryMutation, useDeleteTagCategoryMutation, useGetTagCategoriesQuery, useGetTagsQuery, useDeleteTagMutation, useUpdateTagMutation, useUpdateTagCategoryMutation } from 'src/features/admin/tagging/tagging.slice';
export const Tagging = () => {
  const {
    data: tagCategories
  } = useGetTagCategoriesQuery(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>();
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string>();
  const [totalTagPages, setTotalTagPages] = useState(0);
  const [currentTagPage, setCurrentTagPage] = useState(1);
  const [tagLimit] = useState(10);
  const [tagSkip, setTagSkip] = useState(0);
  const [tagLabel, setTagLabel] = useState<string>('');
  useEffect(() => {
    if (tagCategories?.[0]?._id) {
      setSelectedCategoryId(tagCategories[0]._id);
    }
  }, [tagCategories]);
  const {
    data: fetchTagsResponse
  } = useGetTagsQuery({
    category: selectedCategoryId,
    subcategory: selectedSubcategoryId,
    populate: false,
    skip: tagSkip,
    label: tagLabel
  }, {
    refetchOnMountOrArgChange: true,
    skip: !selectedCategoryId
  });
  useEffect(() => {
    const pages = Math.ceil((fetchTagsResponse?.count ?? 0) / tagLimit);
    setTotalTagPages(pages);
  }, [fetchTagsResponse, tagLimit]);
  const [createTag, {
    data: createdTag,
    isLoading: isCreatingTag,
    reset: resetCreateTag
  }] = useCreateTagMutation();
  const [deleteTag, {
    data: deleteTagResponse
  }] = useDeleteTagMutation();
  const [updateTag] = useUpdateTagMutation();
  const [createTagCategory, {
    data: createdTagCategory,
    isLoading: isCreatingTagCategory,
    reset: resetCreateTagCategory
  }] = useCreateTagCategoryMutation();
  const [deleteTagCategory, {
    data: deleteTagCategoryResponse
  }] = useDeleteTagCategoryMutation();
  const [updateTagCategory] = useUpdateTagCategoryMutation();
  const handleCreateTag = (newTag: CreateTagRequest) => {
    // eslint-disable-next-line no-void
    void createTag(newTag);
  };
  const handleDeleteTag = (tagId: string) => {
    // eslint-disable-next-line no-void
    void deleteTag(tagId);
  };
  const handleUpdateTag = (updateRequest: UpdateTagRequest) => {
    // eslint-disable-next-line no-void
    void updateTag(updateRequest);
  };
  const handleUpdateTagCategory = (updateRequest: UpdateTagCategoryRequest) => {
    // eslint-disable-next-line no-void
    void updateTagCategory(updateRequest);
  };
  const handleCreateTagCategory = (newTagCategory: Omit<TagCategory, '_id' | 'createdBy'>) => {
    // eslint-disable-next-line no-void
    void createTagCategory(newTagCategory);
  };
  const handleDeleteTagCategory = (categoryId: string) => {
    // eslint-disable-next-line no-void
    void deleteTagCategory(categoryId);
  };
  useEffect(() => {
    if (deleteTagResponse && deleteTagResponse.status === 'error') {
      NotificationDispatch('error', deleteTagResponse.message);
    }
  }, [deleteTagResponse]);
  useEffect(() => {
    if (deleteTagCategoryResponse && deleteTagCategoryResponse.status === 'error') {
      NotificationDispatch('error', deleteTagCategoryResponse.message);
    }
  }, [deleteTagCategoryResponse]);
  const handlePageChange = useCallback((page: number) => {
    setTagSkip((page - 1) * tagLimit);
    setCurrentTagPage(page);
  }, [tagLimit]);
  const onLabelChange = (newLabel: string) => {
    setTagLabel(newLabel);
  };
  return <section data-sentry-component="Tagging" data-sentry-source-file="tagging.tsx">
      <SEO title="Admin Manage Tagging" description="Manage tagging page" data-sentry-element="SEO" data-sentry-source-file="tagging.tsx" />
      <Banner right="0" data-sentry-element="Banner" data-sentry-source-file="tagging.tsx">
        <Title titleText="Manage Tagging" subTitleText="Manage tags and tag categories" isInBanner data-sentry-element="Title" data-sentry-source-file="tagging.tsx" />
      </Banner>
      <StyledTaggingContainer data-sentry-element="StyledTaggingContainer" data-sentry-source-file="tagging.tsx">
        <Tabs defaultValue="tags" data-sentry-element="Tabs" data-sentry-source-file="tagging.tsx">
          <TabList data-sentry-element="TabList" data-sentry-source-file="tagging.tsx">
            <Tab value="tags" data-sentry-element="Tab" data-sentry-source-file="tagging.tsx">Tags</Tab>
            <Tab value="tagCategories" data-sentry-element="Tab" data-sentry-source-file="tagging.tsx">Tag Categories</Tab>
          </TabList>
          <TabPanel value="tags" data-sentry-element="TabPanel" data-sentry-source-file="tagging.tsx">
            <StyledTabContent data-sentry-element="StyledTabContent" data-sentry-source-file="tagging.tsx">
              <Tags tags={fetchTagsResponse?.tags || []} tagCategories={tagCategories || []} handleCreateTag={handleCreateTag} handleDeleteTag={handleDeleteTag} handleUpdateTag={handleUpdateTag} isCreating={isCreatingTag} createdTag={createdTag} resetCreateTag={resetCreateTag} selectedCategoryId={selectedCategoryId} setSelectedCategoryId={setSelectedCategoryId} selectedSubcategoryId={selectedSubcategoryId} setSelectedSubcategoryId={setSelectedSubcategoryId} totalPages={totalTagPages} onPageChange={handlePageChange} currentPage={currentTagPage} onLabelChange={onLabelChange} data-sentry-element="Tags" data-sentry-source-file="tagging.tsx" />
            </StyledTabContent>
          </TabPanel>
          <TabPanel value="tagCategories" data-sentry-element="TabPanel" data-sentry-source-file="tagging.tsx">
            <StyledTabContent data-sentry-element="StyledTabContent" data-sentry-source-file="tagging.tsx">
              <TagCategories tagCategories={tagCategories || []} createTagCategory={handleCreateTagCategory} deleteTagCategory={handleDeleteTagCategory} updateTagCategory={handleUpdateTagCategory} isCreating={isCreatingTagCategory} createdTagCategory={createdTagCategory} resetCreateTagCategory={resetCreateTagCategory} data-sentry-element="TagCategories" data-sentry-source-file="tagging.tsx" />
            </StyledTabContent>
          </TabPanel>
        </Tabs>
      </StyledTaggingContainer>
    </section>;
};